import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import { LocalStorageConstants } from "../../../../constants";
import { PortfolioDatesData, PortfolioPhaseDates, SelectData } from "../../../../models";
import { searchPipelineDeals, SearchPipelineDealsRequest } from "../../../../service/query";
import { FilterGroups, ServiceError } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { jsonReviver, safeLocalStorage } from "../../../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../../../widget";
import { hasOverlappingDates } from "./utils";

interface UsePortfolioRoadmapPageReturnData {
  errors: ServiceError[] | undefined;
  data: PortfolioDatesData[];
  dealsMultiSelectData: SelectData | undefined;
  roadmapDealUuids: string[];
  setRoadmapDealUuids: Dispatch<SetStateAction<string[]>>;
  showAddDealsModal: boolean;
  setShowAddDealsModal: Dispatch<SetStateAction<boolean>>;
  closeSelectDealsModal: () => void;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  hasData: boolean;
  onSaveDealsSelectData: () => void;
  fetchSelectData: (name?: string) => Promise<void>;
}

const MAX_DEALS = 15;

export const usePortfolioRoadmapPage = (): UsePortfolioRoadmapPageReturnData => {
  const { user, currentOrganisationUuid } = useAuth();

  const portfolioRoadmapLocalStorageItemName = `${user?.userUuid ? `${user.userUuid}_` : ""}${LocalStorageConstants.PIPELINE_ROADMAP_PREFERENCES}`;

  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ServiceError[]>();
  const [data, setData] = useState<PortfolioDatesData[]>([]);
  const [dealsMultiSelectData, setDealsMultiSelectData] = useState<SelectData>();
  const [roadmapDealUuids, setRoadmapDealUuids] = useState<string[]>([]);
  const [showAddDealsModal, setShowAddDealsModal] = useState(false);

  const hasData = data.length > 0;

  const updatePortfolioRoadmapLocalStorage = useCallback(
    (uuids: string[]) => {
      try {
        safeLocalStorage.setItem(portfolioRoadmapLocalStorageItemName, JSON.stringify(uuids));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err) {
        const message = err instanceof Error ? err.message : "An unknown error occurred while saving to local storage.";
        Toast.warn({ message });
      }
    },
    [portfolioRoadmapLocalStorageItemName]
  );

  const baseDateFilterCriteria = useCallback(() => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    return filterCriteria;
  }, []);

  const fetchSelectData = useCallback(
    async (name?: string) => {
      if (!currentOrganisationUuid) return;

      const filterCriteria = baseDateFilterCriteria();

      if (name) {
        filterCriteria.name = {
          operator: "startsWith",
          value: name,
        };
      }

      const res = await searchPipelineDeals({
        organisationUuid: currentOrganisationUuid,
        paging: {
          limit: 50,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: { results: filterCriteria },
      });

      setDealsMultiSelectData(
        res.data?.results
          .filter((deal) => deal.dealDates !== null && deal.dealDates !== "{}")
          .map((deal) => ({
            key: deal.uuid,
            value: deal.name,
          })) ?? []
      );
    },
    [baseDateFilterCriteria, currentOrganisationUuid]
  );

  const fetchRoadmapData = useCallback(
    async (uuids: string[]) => {
      if (!currentOrganisationUuid || uuids.length === 0) {
        setData([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const filterCriteria = baseDateFilterCriteria();
      filterCriteria.uuid = {
        operator: "in",
        value: uuids,
      };

      const request: SearchPipelineDealsRequest & FilterGroups<SearchPipelineDealsRequest["filter"]> = {
        organisationUuid: currentOrganisationUuid,
        paging: {
          limit: MAX_DEALS,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: { results: filterCriteria },
      };

      const res = await searchPipelineDeals(request);

      const roadmapData: PortfolioDatesData[] =
        res.data?.results.map((deal) => {
          const dates = JSON.parse(deal.dealDates ?? "{}", jsonReviver) as Record<string, PortfolioPhaseDates>;
          const datesOverlap = hasOverlappingDates(dates);

          return {
            uuid: deal.uuid,
            displayName: deal.name,
            subtitle: deal.relatedObjects.map((x) => x.name).join(", "),
            currentStatus: deal.status,
            dates,
            hasOverlappingDates: datesOverlap,
            milestones: [], // TODO update when milestones available
          };
        }) ?? [];

      setData(roadmapData);

      const availableDealUuids = new Set(roadmapData.map((deal) => deal.uuid));
      const filteredUuids = uuids.filter((uuid) => availableDealUuids.has(uuid));

      if (filteredUuids.length !== uuids.length) {
        setRoadmapDealUuids(filteredUuids);
        updatePortfolioRoadmapLocalStorage(filteredUuids);
      }

      setIsLoading(false);
    },
    [baseDateFilterCriteria, currentOrganisationUuid, updatePortfolioRoadmapLocalStorage]
  );

  const fetchAndSetRoadmapDealUuids = useCallback(() => {
    const storedDeals = safeLocalStorage.getItem(portfolioRoadmapLocalStorageItemName);
    if (storedDeals) {
      const uuids = JSON.parse(storedDeals) as string[];
      setRoadmapDealUuids(uuids);
      fetchRoadmapData(uuids);
    } else {
      setIsLoading(false);
    }
  }, [fetchRoadmapData, portfolioRoadmapLocalStorageItemName]);

  const closeSelectDealsModal = useCallback(() => {
    setShowAddDealsModal(false);
    fetchAndSetRoadmapDealUuids();
  }, [fetchAndSetRoadmapDealUuids]);

  const onSaveDealsSelectData = useCallback(() => {
    setErrors(undefined);
    if (roadmapDealUuids.length > MAX_DEALS) {
      setErrors([{ code: "ERROR_15_DEALS_MAXIMUM", message: "Please select no more than 15 deals" }]);
      return;
    }
    const limitedUuids = roadmapDealUuids.slice(0, MAX_DEALS);
    updatePortfolioRoadmapLocalStorage(limitedUuids);
    fetchRoadmapData(limitedUuids);
    setShowAddDealsModal(false);
  }, [fetchRoadmapData, roadmapDealUuids, updatePortfolioRoadmapLocalStorage]);

  useEffect(() => {
    fetchAndSetRoadmapDealUuids();
    fetchSelectData();
  }, [fetchAndSetRoadmapDealUuids, fetchSelectData]);

  return {
    errors,
    data,
    dealsMultiSelectData,
    roadmapDealUuids,
    setRoadmapDealUuids,
    showAddDealsModal,
    setShowAddDealsModal,
    closeSelectDealsModal,
    isLoading,
    setIsLoading,
    hasData,
    onSaveDealsSelectData,
    fetchSelectData,
  };
};
